import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";
import { apiStakeJobs, apiStakeStatus, apiStakeSubmitRender } from "@/lib/api";

/** Stake Drawer — bridge stub. */
export default function StakeDrawer({ open, onClose }) {
    const [status, setStatus] = useState({ connected: false, message: "Awaiting Stake migration" });
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try { setStatus(await apiStakeStatus()); } catch { /* offline */ }
            try { setJobs((await apiStakeJobs()).jobs || []); } catch { /* offline */ }
        })();
    }, [open]);

    const testRender = async () => {
        setBusy(true);
        try {
            const job = await apiStakeSubmitRender({ type: "render", params: { test: true } });
            setJobs((js) => [job, ...js]);
        } catch { /* swallow */ }
        finally { setBusy(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }} onClick={onClose}
                    data-testid="stake-drawer-backdrop"
                    className="fixed inset-0 z-[55] bg-tca-bg-deep/55 backdrop-blur-sm"
                >
                    <motion.aside
                        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                        transition={{ type: "spring", stiffness: 250, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="stake-drawer"
                        className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-tca-bg-panel/95 backdrop-blur-xl flex flex-col"
                        style={{ boxShadow: "0 0 60px rgba(0,219,255,0.18)" }}
                    >
                        <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={14} borderTint="rgba(0,219,255,0.22)" className="h-full flex flex-col" innerClassName="flex flex-col h-full">
                            <div className="px-5 py-4 border-b border-tca-cyan/15 flex items-center justify-between">
                                <span className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/90">// creator's stake bridge</span>
                                <button onClick={onClose} data-testid="stake-drawer-close" className="text-tca-ink-muted hover:text-tca-cyan p-1"><X size={16} /></button>
                            </div>

                            <div className="px-5 py-5 space-y-5 overflow-y-auto flex-1">
                                <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={10} borderTint="rgba(0,219,255,0.18)" className="bg-tca-bg-deep/45" innerClassName="p-4">
                                    <div className="flex items-center gap-3">
                                        <motion.span
                                            animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2.2, repeat: Infinity }}
                                            className={`inline-block w-1.5 h-1.5 rounded-full ${status.connected ? "bg-tca-cyan" : "bg-tca-gold-warm"}`}
                                            style={{ boxShadow: status.connected ? "0 0 8px rgba(0,219,255,0.7)" : "0 0 8px rgba(201,168,106,0.5)" }}
                                        />
                                        <div>
                                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">// connection</div>
                                            <div data-testid="stake-status-message" className="text-sm text-tca-ink">
                                                {status.connected ? "BRIDGED" : "AWAITING ECOSYSTEM SYNC"}
                                            </div>
                                            <div className="text-[11px] text-tca-ink-muted mt-1">{status.message}</div>
                                        </div>
                                    </div>
                                </HudFrame>

                                <div>
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85 mb-3">// render jobs</div>
                                    <div data-testid="stake-jobs-list" className="space-y-2">
                                        {jobs.length === 0 ? (
                                            <div className="text-tca-ink-muted text-xs">// no active render jobs</div>
                                        ) : (
                                            jobs.map((j) => (
                                                <div key={j.id} className="border border-tca-cyan/15 bg-tca-bg-deep/45 px-3 py-2 text-xs flex items-center justify-between">
                                                    <span className="text-tca-ink truncate">{j.type} · {j.id.slice(0, 8)}</span>
                                                    <span className={`font-mono-tight text-[10px] uppercase tracking-[0.22em] ${j.status === "completed" ? "text-tca-cyan-hi" : "text-tca-gold-warm"}`}>{j.status}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <button
                                    onClick={testRender} disabled={busy}
                                    data-testid="stake-test-render"
                                    className="w-full inline-flex items-center justify-center gap-2 font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs py-3 border border-tca-cyan/55 text-tca-cyan hover:border-tca-cyan hover:text-tca-cyan-hi rounded-full transition-all"
                                >
                                    {busy && <Loader2 size={12} className="animate-spin" />}
                                    Test render (mock)
                                </button>
                            </div>

                            <div className="px-5 py-3 border-t border-tca-cyan/15 font-mono-tight text-[9px] uppercase tracking-[0.24em] text-tca-ink-muted text-center">
                                // stake migration in progress
                            </div>
                        </HudFrame>
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
